import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { showSuccessToast } from '@/lib/toast';
import { playerTournamentUrl } from '@/lib/appSurface';
import type { Tournament } from '@swiss-magic/shared-types';

// Story 082 — public preview. Surfaces the public spectator URL
// for the tournament with copy + QR + open-in-new-tab.

interface Props {
  tournament: Tournament;
}

export function PublicPreviewPanel({ tournament }: Props) {
  const { t } = useTranslation('director');
  // Use the shared appSurface helper so the URL respects the current
  // tenant's player host (e.g. bgplayerstest.backgammon.pl) rather
  // than hardcoding the director-app host.
  const publicUrl = playerTournamentUrl(tournament.slug);
  // A draft has no live public page yet, so showing the link/QR would
  // hand out a dead URL. Publish first.
  const isLive = tournament.status !== 'draft';

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(publicUrl);
      showSuccessToast(t('tournament_hub.panels.public_preview.link_copied_toast'));
    } catch {
      /* clipboard API failure is silent — the link is still visible */
    }
  }

  return (
    <Card data-testid="tournament-hub-panel-public-preview">
      <CardHeader>
        <CardTitle className="text-base">
          {t('tournament_hub.panels.public_preview.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!isLive ? (
          <p
            data-testid="tournament-hub-public-draft-hint"
            className="text-sm text-muted-foreground"
          >
            {t('tournament_hub.panels.public_preview.draft_no_link_hint')}
          </p>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
            <div className="flex-1 space-y-2">
              <div
                data-testid="tournament-hub-public-link"
                className="break-all rounded border bg-muted/30 p-2 font-mono text-xs"
              >
                {publicUrl}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="tournament-hub-copy-link-button"
                  onClick={copyLink}
                >
                  <Copy className="mr-1 h-3 w-3" />
                  {t('tournament_hub.panels.public_preview.copy_link')}
                </Button>
                <Button asChild size="sm" variant="outline">
                  <a
                    href={publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="tournament-hub-public-open-button"
                  >
                    <ExternalLink className="mr-1 h-3 w-3" />
                    {t('tournament_hub.panels.public_preview.open')}
                  </a>
                </Button>
              </div>
            </div>
            <div
              data-testid="tournament-hub-qr-code"
              className="rounded border bg-white p-2"
            >
              <QRCodeSVG value={publicUrl} size={120} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
